import { LanguageCodeEnum } from '../common/enums/common.enum';

/**
 * The interface languages this panel ships with.
 *
 * These are the same codes the rest of the platform uses for content (`srb`, `eng`), but the
 * choice here is the admin panel's alone: it is not read from, or written to, the user's
 * account, and it does not touch the mobile app. Serbian is Latin script; Cyrillic is not
 * offered yet.
 *
 * Adding a language is: a JSON file next to the others, an entry here, and its MUI / dayjs
 * locale in `locales.ts`. The parity test fails until every key exists in every language.
 */
export const UI_LANGUAGES = [LanguageCodeEnum.SRB, LanguageCodeEnum.ENG] as const;

export type UiLanguage = (typeof UI_LANGUAGES)[number];

export const isUiLanguage = (value: unknown): value is UiLanguage => UI_LANGUAGES.includes(value as UiLanguage);

/**
 * The language the panel opens in, set per deployment in `.env`:
 *
 *   REACT_APP_DEFAULT_LANGUAGE=srb
 *
 * Anything missing or unrecognised falls back to Serbian, so a typo in the setting cannot
 * leave the panel showing raw translation keys.
 */
export const DEFAULT_UI_LANGUAGE: UiLanguage = isUiLanguage(process.env.REACT_APP_DEFAULT_LANGUAGE)
  ? process.env.REACT_APP_DEFAULT_LANGUAGE
  : LanguageCodeEnum.SRB;

/**
 * The code i18next is given for each language. Not `srb`/`eng`: i18next picks plural forms
 * through Intl.PluralRules, which does not know `srb` and silently falls back to English
 * rules - "2 stavki" instead of "2 stavke". `sr-Latn` is the real tag, and names the
 * script, which leaves room for `sr-Cyrl` beside it later.
 */
export const I18N_LANGUAGE_CODES: Record<UiLanguage, string> = {
  [LanguageCodeEnum.SRB]: 'sr-Latn',
  [LanguageCodeEnum.ENG]: 'en'
};

export const uiLanguageFromI18n = (code: string | undefined): UiLanguage =>
  UI_LANGUAGES.find(language => I18N_LANGUAGE_CODES[language] === code) ?? DEFAULT_UI_LANGUAGE;
